import { useParams } from 'react-router'
import { RowData } from '../../../../../baseComponents/TwinTable/types'
import { FormElement, FormRender } from '../../../../../forms/FormRenderer/types'
import { BillingGroupCustomerRelModelType } from '@teinor/erp/types/company/config/billingGroup/billingGroupCustomerRel'
import { LittleModalCreateEdit } from '../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { valueOrDefaultValue } from '../../../../../utils/globals/valueOrDefaultValue'
import { BillingGroupPayload, DictionaryBillingGroupTy } from '../../../../../utils/reducers/company/billingGroups'
import { AllReduxPayloads } from '../../../../../utils/reducers'
import { connect, ConnectedProps } from 'react-redux'
import { ModalEditComponentProps } from '../../../../../baseComponents/ModalsLayouts/types'
import useTwinTranslation from '../../../../../utils/hooks/useTwinTranslation'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { twinFetchPostJSON } from '../../../../../utils/globals/data'
import { BeforeSubmitHandler } from '../../../../../forms/TwinForm/types'
import withLoading from '../../../../../utils/hoc/withLoading'
import { permissionCheck } from '../../../../../baseComponents/PermissionChecker/function'
import { TwinDictionary } from '../../../../../utils/globals/dictionary'

type ModalCECustomerBillingGroupProps = ModalEditComponentProps & ReduxModalCECustomerBillingGroupConnector & {}

const ModalCECustomerBillingGroup: React.FC<ModalCECustomerBillingGroupProps> = ({ userPermissions, allRowData, billingGroups, ...rest }) => {
    const { t } = useTwinTranslation()
    const { handleSelectedBillingGroupId, dictBillingGroups, id, selectedBillingGroups, selectedBillingGroupId, parseBTOBeforeSubmit } = useModalCECustomerBillingGroupsLogic({ billingGroups: billingGroups?.dictionaryBillingGroup || {}, allRowData })
    const extraBillingTypesSell: FormElement<any>[] = []
    if (selectedBillingGroupId) {
        const billingTypesSell = billingGroups?.dictionaryBillingGroup?.[selectedBillingGroupId].billingTypesSellDict || {}
        if (billingTypesSell && Object.keys(billingTypesSell).length) {
            let myDefaultValues: TwinDictionary = {}
            if (allRowData?.BillingTypeSellOptions){
                for (const option of allRowData?.BillingTypeSellOptions) {
                    const myOptionComplete = billingGroups?.billingTypesAllSellOptions[option]
                    myDefaultValues[myOptionComplete?.BillingTypeSellId || 0] = option
                }
            }
            for (const idParent in billingTypesSell) {
                const type = billingTypesSell[idParent];
                let defaultValue = myDefaultValues[idParent] || type.DefaultBillingTypeSellOptionId
                extraBillingTypesSell.push({
                    label: type.name + ' - ' + t('default', 'Por defecto'),
                    name: 'BTO_' + type.id,
                    component: 'CustomSelectWithSearchBar',
                    items: type.billingTypeSellOptionsDict || {},
                    required: type.mandatory,
                    value: defaultValue
                })
            }
        }
    }
    const billingGroup: FormElement<keyof BillingGroupCustomerRelModelType>[] = []

    if (allRowData?.BillingGroupId) {
        billingGroup.push(
            {
                name: 'BillingGroupId',
                label: t('billingGroup', 'Grupo de facturación'),
                items: dictBillingGroups,
                component: 'CustomSelect',
                required: true,
                readOnly: true,
                onChange: handleSelectedBillingGroupId
            },
        )
    } else {
        billingGroup.push(
            {
                name: 'BillingGroupId',
                label: t('billingGroup', 'Grupo de facturación'),
                items: dictBillingGroups,
                component: 'CustomSelectWithNotInFilteredItems',
                excludeItems: selectedBillingGroups,
                required: true,
                readOnly: selectedBillingGroups.length === Object.keys(dictBillingGroups).length ? true : false,
                onChange: handleSelectedBillingGroupId
            },
        )
    }

    const fields: FormRender<BillingGroupCustomerRelModelType> = [
        {
            cols: 1,
            elements: [
                ...billingGroup,
                {
                    name: 'CustomerId',
                    component: 'InputHidden',
                    value: id
                },
                ...extraBillingTypesSell
            ],
        }
    ]
    if (allRowData && userPermissions) {
        const parsedFields = valueOrDefaultValue(fields, userPermissions, allRowData)
        return (<LittleModalCreateEdit beforeSubmit={parseBTOBeforeSubmit} fields={parsedFields} url={'/api/app/customer/customerBillingGroup/updateBillingGroupCustomer'} translations={{ title: t('editCustomerBillingGroup', 'Editar Grupo de Facturación de cliente'), button: t('save', 'Guardar') }} haveButtonPermissions={permissionCheck(userPermissions, 'update')} {...rest} />)
    }

    return (<LittleModalCreateEdit beforeSubmit={parseBTOBeforeSubmit} fields={fields} url={'/api/app/customer/customerBillingGroup/assignBillingGroupToCustomer'} translations={{
        title: t('createCustomerBillingGroup', 'Crear Grupo de Facturación de cliente'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} {...rest} />)
}
interface ModalCECustomerBillingGroupLogicProps {
    billingGroups: DictionaryBillingGroupTy
    allRowData?: RowData
}

const useModalCECustomerBillingGroupsLogic = ({ billingGroups, allRowData }: ModalCECustomerBillingGroupLogicProps) => {
    const [selectedBillingGroups, setSelectedBillingGroups] = useState<string[]>([])
    const [selectedBillingGroupId, setSelectedBillingGroupId] = useState<number | null>(null)
    const { id } = useParams()
    const handleSelectedBillingGroupId = useCallback((BillingGroupId: number) => {
        setSelectedBillingGroupId(BillingGroupId)
    }, [setSelectedBillingGroupId])

    const dictBillingGroups = useMemo(() => billingGroups || {}, [billingGroups])
    const getSelectedBillingGroups = useCallback(async () => {
        const result = await twinFetchPostJSON('/api/app/customer/customerBillingGroup/getAllBillingGroupsCustomers', { where: { 'CustomerId': id } })
        const selected: string[] = []
        for (const selectedBillingGroup of result) {
            selected.push(String(selectedBillingGroup.BillingGroupId))
        }
        setSelectedBillingGroups(selected)
    }, [setSelectedBillingGroups, id])

    const parseBTOBeforeSubmit: BeforeSubmitHandler = useCallback((vals) => {
        const result: number[] = []
        if (selectedBillingGroupId) {
            const billingTypesSell = dictBillingGroups[selectedBillingGroupId].billingTypesSellDict
            for (const key in billingTypesSell) {
                const billingType = billingTypesSell[parseInt(String(key))]
                const index = 'BTO_' + billingType.id
                const element = vals[index];
                result.push(element)
            }
            vals['BillingTypeSellOptions'] = result
            return true
        }
        return false
    }, [selectedBillingGroupId, dictBillingGroups])

    useEffect(() => {
        getSelectedBillingGroups()
    }, [getSelectedBillingGroups])

    useEffect(() => {
        if (allRowData?.BillingGroupId) {
            setSelectedBillingGroupId(allRowData?.BillingGroupId)
        }
    }, [allRowData?.BillingGroupId])

    return { id, selectedBillingGroups, handleSelectedBillingGroupId, selectedBillingGroupId, parseBTOBeforeSubmit, dictBillingGroups }
}

const modalCECustomerBillingGroupDispatch = {
    setBillingGroups: (payload: BillingGroupPayload) => ({ type: 'CHANGE_BILLINGGROUP', payload }),
}
const mapModalCECustomerBillingGroupConnector = (state: AllReduxPayloads) => ({ billingGroups: state.billingGroups })
const modalCECustomerBillingGroupConnector = connect(mapModalCECustomerBillingGroupConnector, modalCECustomerBillingGroupDispatch)
type ReduxModalCECustomerBillingGroupConnector = ConnectedProps<typeof modalCECustomerBillingGroupConnector>

const modalCECustomerBillingGroupConnectLoading = withLoading(ModalCECustomerBillingGroup, [{ fetchUrl: '/api/app/config/billingGroup/getAllBillingGroupsComplete', propName: 'billingGroups', setFunctionName: 'setBillingGroups' }])
const ModalCECustomerBillingGroupConnect = modalCECustomerBillingGroupConnector(modalCECustomerBillingGroupConnectLoading)
export default ModalCECustomerBillingGroupConnect
